import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Card,
  Alert,
  message,
  Row,
  Col,
  Typography,
  Tag,
  Tooltip,
  Switch,
  Upload,
  Divider,
  Table,
  Popconfirm,
  Collapse,
  InputNumber,
  Slider,
} from 'antd';
import {
  EditOutlined,
  LinkOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  UploadOutlined,
  DeleteOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { v2ToppingsApi } from '../../../../api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const EditToppingWizard = ({ visible, topping, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvColumns, setCsvColumns] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [csvCurrentPage, setCsvCurrentPage] = useState(1);
  const [csvPageSize] = useState(20);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  // カテゴリの日本語表示名を取得
  const getCategoryDisplayName = category => {
    const categoryMap = {
      'cta': 'CTA・行動喚起',
      'product': '商品・サービス情報',
      'case_study': '事例・実績',
      'faq': 'よくある質問',
      'statistics': '統計・データ',
      'testimonial': '口コミ・評価',
      'company': '会社情報',
      'support': 'サポート・問い合わせ',
      'other': 'その他',
    };
    return categoryMap[category] || category;
  };

  // 初期化
  useEffect(() => {
    if (visible && topping) {
      // タグを取得
      fetchUserTags();

      // デバッグログ: 受け取ったtoppingデータを確認
      console.log('EditToppingWizard received topping:', topping);
      console.log('Topping URL fields:', {
        source_url: topping.source_url,
        raw_data: topping.raw_data,
        url: topping.url,
        type: topping.type,
      });

      // フォームに既存データを設定
      form.setFieldsValue({
        title: topping.title,
        description: topping.description || '',
        category: topping.category || '',
        tags: topping.tags || [],
        is_active: topping.is_active !== false,
        content: topping.content || '',
        rag_enabled: topping.rag_enabled !== false,
        chunk_size: topping.rag_chunk_size || 1000,
        chunk_overlap: topping.rag_overlap || 200,
        strength: topping.reliability || 1.0,
      });

      // CSVデータの場合、テーブル形式に変換
      if (topping.type === 'csv' && topping.content) {
        console.log('Initializing CSV data for editing:', {
          contentLength: topping.content.length,
          contentPreview: topping.content.substring(0, 200),
        });

        const { columns, data } = parseCsvToTable(topping.content);

        console.log('CSV initialization result:', {
          columnsCount: columns.length,
          dataCount: data.length,
        });

        setCsvColumns(columns);
        setCsvData(data);
      } else if (topping.type === 'csv') {
        console.log('CSV topping has no content');
        setCsvColumns([]);
        setCsvData([]);
      }
    }
  }, [visible, topping, form]);

  // ユーザーのタグを取得
  const fetchUserTags = async () => {
    try {
      setTagsLoading(true);
      const response = await v2ToppingsApi.getTags();
      setAvailableTags(response.data?.tags || []);
    } catch (error) {
      console.error('Failed to fetch user tags:', error);
    } finally {
      setTagsLoading(false);
    }
  };

  // 閉じる処理
  const handleClose = () => {
    form.resetFields();
    setCsvData([]);
    setCsvColumns([]);
    setFileList([]);
    setCsvCurrentPage(1);
    onCancel();
  };

  // 更新処理
  const handleUpdate = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const updateData = {
        title: values.title,
        description: values.description || '',
        category: values.category || '',
        tags: values.tags || [],
        is_active: values.is_active,
      };

      // テキストタイプの場合はコンテンツも更新
      if (topping.type === 'text') {
        updateData.content = values.content;
      }

      // CSVタイプの場合はCSVデータも更新
      if (topping.type === 'csv' && csvData.length > 0) {
        const csvContent = tableDataToCsv(csvColumns, csvData);
        console.log('Saving CSV content:', {
          originalRows: csvData.length,
          originalColumns: csvColumns.length - 1, // アクション列を除く
          contentLength: csvContent.length,
          contentPreview: csvContent.substring(0, 200),
        });
        updateData.content = csvContent;
      } else if (topping.type === 'csv') {
        console.log('No CSV data to save');
      }

      // RAG設定の更新（CSVとPDFの場合）
      if (topping.type === 'csv' || topping.type === 'pdf') {
        updateData.rag_enabled = values.rag_enabled !== false;
        updateData.rag_chunk_size = values.chunk_size || 1000;
        updateData.rag_overlap = values.chunk_overlap || 200;
        updateData.reliability = values.strength || 1.0;
      }

      await v2ToppingsApi.updateTopping(topping.id, updateData);

      message.success('独自情報を更新しました');
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Update error:', error);
      message.error(`更新に失敗しました: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // アイコン取得
  const getTypeIcon = type => {
    switch (type) {
      case 'csv':
        return <FileExcelOutlined style={{ color: '#52c41a' }} />;
      case 'pdf':
        return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
      case 'url':
        return <LinkOutlined style={{ color: '#1890ff' }} />;
      case 'text':
        return <FileTextOutlined style={{ color: '#722ed1' }} />;
      default:
        return <FileTextOutlined />;
    }
  };

  // CSV文字列を正確に解析する関数
  const parseCsvToTable = csvContent => {
    if (!csvContent || typeof csvContent !== 'string') {
      console.log('CSV content is empty or invalid:', csvContent);
      return { columns: [], data: [] };
    }

    try {
      // より正確なCSV解析（カンマが含まれるセルにも対応）
      const parseCSVLine = line => {
        const result = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
          const char = line[i];

          if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
              // ダブルクォートのエスケープ
              current += '"';
              i++; // 次の文字をスキップ
            } else {
              // クォートの開始/終了
              inQuotes = !inQuotes;
            }
          } else if (char === ',' && !inQuotes) {
            // フィールドの区切り
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }

        // 最後のフィールドを追加
        result.push(current.trim());
        return result;
      };

      const lines = csvContent
        .trim()
        .split('\n')
        .filter(line => line.trim());
      if (lines.length === 0) {
        console.log('No valid CSV lines found');
        return { columns: [], data: [] };
      }

      // ヘッダー行を解析
      const headers = parseCSVLine(lines[0]);
      console.log('Parsed CSV headers:', headers);

      // 列定義を作成
      const columns = headers.map((header, index) => ({
        title: header || `列${index + 1}`,
        dataIndex: `col_${index}`,
        key: `col_${index}`,
        width: 150,
        ellipsis: true,
        render: (text, record, rowIndex) => (
          <Input
            value={text || ''}
            onChange={e =>
              handleCellChange(e.target.value, record.key, `col_${index}`)
            }
            onBlur={() => {
              // フォーカスが外れた時の処理（必要に応じて）
              console.log('Cell blur:', record.key, `col_${index}`, text);
            }}
            size="small"
            style={{ width: '100%' }}
          />
        ),
      }));

      // アクション列を追加
      columns.push({
        title: 'アクション',
        key: 'action',
        width: 80,
        fixed: 'right',
        render: (_, record) => (
          <Popconfirm
            title="この行を削除しますか？"
            onConfirm={() => deleteCsvRow(record.key)}
            okText="削除"
            cancelText="キャンセル"
            placement="topRight"
          >
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              title="行を削除"
            />
          </Popconfirm>
        ),
      });

      // データ行を解析
      const data = lines.slice(1).map((line, index) => {
        const values = parseCSVLine(line);
        const row = {
          key: `row_${index}_${Date.now()}`, // より一意なキーを生成
          _originalIndex: index, // 元のインデックスを保持
        };

        headers.forEach((_, colIndex) => {
          row[`col_${colIndex}`] = values[colIndex] || '';
        });

        return row;
      });

      console.log('Parsed CSV data:', {
        columnsCount: columns.length - 1, // アクション列を除く
        dataCount: data.length,
        sampleData: data.slice(0, 3),
      });

      return { columns, data };
    } catch (error) {
      console.error('CSV parsing error:', error);
      message.error('CSVの解析中にエラーが発生しました');
      return { columns: [], data: [] };
    }
  };

  // セル変更処理（最適化版）
  const handleCellChange = (value, rowKey, dataIndex) => {
    console.log('Cell change:', { value, rowKey, dataIndex });

    setCsvData(prevData => {
      const newData = prevData.map(item => {
        if (item.key === rowKey) {
          return {
            ...item,
            [dataIndex]: value,
          };
        }
        return item;
      });

      console.log(
        'Updated CSV data:',
        newData.find(item => item.key === rowKey),
      );
      return newData;
    });
  };

  // 行削除（最適化版）
  const deleteCsvRow = rowKey => {
    console.log('Deleting row:', rowKey);

    setCsvData(prevData => {
      const newData = prevData.filter(item => item.key !== rowKey);
      console.log('Remaining rows after deletion:', newData.length);
      return newData;
    });
  };

  // 行追加（最適化版）
  const addCsvRow = () => {
    const newKey = `row_new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newRow = {
      key: newKey,
      _isNew: true, // 新規追加行であることを示すフラグ
    };

    // 既存の列に合わせて空の値を設定
    csvColumns.forEach(col => {
      if (col.dataIndex && col.dataIndex !== 'action') {
        newRow[col.dataIndex] = '';
      }
    });

    console.log('Adding new row:', newRow);

    setCsvData(prevData => {
      const newData = [...prevData, newRow];
      console.log('Total rows after addition:', newData.length);
      return newData;
    });
  };

  // テーブルデータをCSV形式に変換（最適化版）
  const tableDataToCsv = (columns, data) => {
    if (!columns || !data || columns.length === 0 || data.length === 0) {
      console.log('Invalid data for CSV conversion:', {
        columns: columns?.length,
        data: data?.length,
      });
      return '';
    }

    try {
      // ヘッダー行を作成（アクション列は除外）
      const dataColumns = columns.filter(
        col => col.dataIndex && col.key !== 'action',
      );
      const headers = dataColumns.map(col => col.title);

      console.log('CSV conversion - Headers:', headers);

      // データ行を作成
      const rows = data.map((row, index) => {
        const rowData = dataColumns.map(col => {
          const value = row[col.dataIndex] || '';
          // CSVエスケープ処理
          if (
            value.includes(',') ||
            value.includes('"') ||
            value.includes('\n')
          ) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        });

        if (index < 3) {
          // 最初の3行をログ出力
          console.log(`CSV conversion - Row ${index}:`, rowData);
        }

        return rowData;
      });

      // CSV形式に変換
      const csvLines = [headers, ...rows];
      const csvContent = csvLines.map(line => line.join(',')).join('\n');

      console.log('CSV conversion completed:', {
        headers: headers.length,
        rows: rows.length,
        totalLength: csvContent.length,
      });

      return csvContent;
    } catch (error) {
      console.error('CSV conversion error:', error);
      message.error('CSVの変換中にエラーが発生しました');
      return '';
    }
  };

  // ファイル再アップロード処理
  const handleFileUpload = async file => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', form.getFieldValue('title'));
      formData.append('description', form.getFieldValue('description') || '');
      formData.append('category', form.getFieldValue('category') || '');
      formData.append('tags', JSON.stringify(form.getFieldValue('tags') || []));
      formData.append('auto_process', 'true');

      await v2ToppingsApi.reuploadTopping(topping.id, formData);

      message.success('ファイルの再アップロードが完了しました');
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('File reupload error:', error);
      message.error(`ファイルの再アップロードに失敗しました: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // リッチテキストエディタ（テキストタイプ用）
  const RichTextEditor = () => {
    const [localContent, setLocalContent] = useState(topping?.content || '');
    const textAreaRef = useRef(null);

    // 初期化時にtoppingのcontentを設定
    useEffect(() => {
      if (topping?.content) {
        setLocalContent(topping.content);
      }
    }, [topping?.content]);

    const insertText = useCallback(
      (before, after = '', placeholder = '') => {
        const textarea = textAreaRef.current?.resizableTextArea?.textArea;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = localContent.substring(start, end);
        const textToInsert = selectedText || placeholder;

        const newText =
          localContent.substring(0, start) +
          before +
          textToInsert +
          after +
          localContent.substring(end);

        // ローカル状態とフォームを同期更新
        setLocalContent(newText);
        form.setFieldsValue({ content: newText });

        // カーソル位置を調整
        setTimeout(() => {
          const newCursorPos = start + before.length + textToInsert.length;
          textarea.focus();
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      },
      [localContent, form],
    );

    const handleHeading = () => insertText('# ', '', '見出しテキスト');
    const handleBulletList = () => {
      const lines = localContent.split('\n');
      const textarea = textAreaRef.current?.resizableTextArea?.textArea;
      const start = textarea?.selectionStart || 0;

      // 現在の行を取得
      let currentLineStart = localContent.lastIndexOf('\n', start - 1) + 1;
      if (currentLineStart === 0 && start === 0) currentLineStart = 0;

      insertText('\n- ', '', '項目');
    };
    const handleLink = () => insertText('[', '](URL)', 'リンクテキスト');
    const handleImage = () => insertText('![', '](画像URL)', '画像の説明');

    const handleContentChange = useCallback(
      e => {
        const newContent = e.target.value;
        setLocalContent(newContent);
        form.setFieldsValue({ content: newContent });
      },
      [form],
    );

    return (
      <div>
        <Form.Item
          label="コンテンツ"
          name="content"
          rules={[
            { required: true, message: 'コンテンツを入力してください' },
            { min: 10, message: 'コンテンツは10文字以上入力してください' },
          ]}
        >
          <div>
            {/* ツールバー */}
            <div
              style={{
                marginBottom: 8,
                padding: '8px 12px',
                backgroundColor: '#fafafa',
                border: '1px solid #d9d9d9',
                borderRadius: '6px 6px 0 0',
                borderBottom: 'none',
              }}
            >
              <Space size="small">
                <Tooltip title="見出し">
                  <Button
                    size="small"
                    icon={<FileTextOutlined />}
                    onClick={handleHeading}
                  >
                    見出し
                  </Button>
                </Tooltip>
                <Tooltip title="箇条書き">
                  <Button
                    size="small"
                    icon={
                      <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        •
                      </div>
                    }
                    onClick={handleBulletList}
                  >
                    箇条書き
                  </Button>
                </Tooltip>
                <Tooltip title="リンク">
                  <Button
                    size="small"
                    icon={<LinkOutlined />}
                    onClick={handleLink}
                  >
                    リンク
                  </Button>
                </Tooltip>
                <Tooltip title="画像">
                  <Button
                    size="small"
                    icon={<FileTextOutlined />}
                    onClick={handleImage}
                  >
                    画像
                  </Button>
                </Tooltip>
              </Space>
            </div>

            {/* テキストエリア */}
            <TextArea
              ref={textAreaRef}
              rows={12}
              placeholder="ここにコンテンツを入力してください..."
              value={localContent}
              onChange={handleContentChange}
              style={{
                fontSize: '14px',
                lineHeight: '1.6',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                borderRadius: '0 0 6px 6px',
              }}
              showCount
              maxLength={10000}
            />
          </div>
        </Form.Item>

        {/* 使い方ガイド */}
        <Alert
          message="💡 使い方"
          description="上のボタンをクリックして装飾を追加できます。テキストを選択してからボタンを押すと、選択したテキストが装飾されます。"
          type="info"
          showIcon={false}
          style={{
            marginBottom: 16,
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
          }}
        />
      </div>
    );
  };

  if (!topping) return null;

  return (
    <Modal
      title={
        <Space>
          {getTypeIcon(topping.type)}
          <span>独自情報の編集</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={1000}
      destroyOnClose
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          padding: '20px 0',
        },
      }}
    >
      <div style={{ padding: '20px 24px 0' }}>
        <Form form={form} layout="vertical">
          {/* 基本情報 */}
          <Card title="基本情報" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="タイトル"
                  name="title"
                  rules={[
                    { required: true, message: 'タイトルを入力してください' },
                  ]}
                >
                  <Input placeholder="独自情報のタイトル" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="category" label="カテゴリー">
                  <Select placeholder="カテゴリーを選択" allowClear>
                    <Option value="cta">CTA・行動喚起</Option>
                    <Option value="product">商品・サービス情報</Option>
                    <Option value="case_study">事例・実績</Option>
                    <Option value="faq">よくある質問</Option>
                    <Option value="statistics">統計・データ</Option>
                    <Option value="testimonial">口コミ・評価</Option>
                    <Option value="company">会社情報</Option>
                    <Option value="support">サポート・問い合わせ</Option>
                    <Option value="other">その他</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="tags" label="タグ">
                  <Select
                    mode="tags"
                    placeholder="タグを入力（Enterで確定）"
                    style={{ width: '100%' }}
                    loading={tagsLoading}
                    notFoundContent={
                      tagsLoading ? 'タグを読み込み中...' : '新しいタグを入力'
                    }
                  >
                    {availableTags.map(tag => (
                      <Option key={tag} value={tag}>
                        {tag}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="説明">
              <TextArea rows={3} placeholder="独自情報の説明（任意）" />
            </Form.Item>

            <Form.Item
              name="is_active"
              label="ステータス"
              valuePropName="checked"
            >
              <Switch checkedChildren="アクティブ" unCheckedChildren="無効" />
            </Form.Item>
          </Card>

          {/* ソース情報 */}
          <Card title="ソース情報" style={{ marginBottom: 24 }}>
            {topping.type === 'url' && (
              <div>
                <Text type="secondary">URL（変更不可）</Text>
                <div
                  style={{
                    marginTop: 8,
                    padding: 12,
                    backgroundColor: '#fafafa',
                    borderRadius: 4,
                    border: '1px solid #d9d9d9',
                  }}
                >
                  <Text copyable>
                    {topping.source_url ||
                      topping.raw_data ||
                      topping.url ||
                      (topping.content_metadata &&
                        topping.content_metadata.url) ||
                      'URL情報なし'}
                  </Text>
                </div>
                <Alert
                  message="URL情報について"
                  description="URLは変更できません。記事生成時に最新のコンテンツをリアルタイムで取得します。"
                  type="info"
                  showIcon
                  style={{ marginTop: 12 }}
                />
              </div>
            )}

            {topping.type === 'text' && (
              <div>
                <Text type="secondary">コンテンツ編集</Text>
                <div style={{ marginTop: 8 }}>
                  <RichTextEditor />
                </div>
              </div>
            )}

            {topping.type === 'csv' &&
              csvColumns.length > 0 &&
              csvData.length > 0 && (
                <div>
                  <div
                    style={{
                      marginBottom: 16,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Space>
                      <Text type="secondary">CSVデータ編集</Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        ({csvData.length}行 × {csvColumns.length - 1}列)
                      </Text>
                    </Space>
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={addCsvRow}
                      size="small"
                    >
                      行を追加
                    </Button>
                  </div>
                  <Alert
                    message="編集のヒント"
                    description="セルをクリックして直接編集できます。行の削除は右端のアクションボタンから行えます。"
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    closable
                  />
                  <Table
                    columns={csvColumns}
                    dataSource={csvData}
                    rowKey="key"
                    pagination={{
                      current: csvCurrentPage,
                      pageSize: csvPageSize,
                      total: csvData.length,
                      onChange: page => setCsvCurrentPage(page),
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} / ${total}件`,
                      pageSizeOptions: ['10', '20', '50', '100'],
                      size: 'small',
                    }}
                    scroll={{
                      x: Math.max(1000, csvColumns.length * 150),
                      y: 400,
                    }}
                    size="small"
                    bordered
                    loading={loading}
                    locale={{
                      emptyText: 'データがありません',
                    }}
                  />
                </div>
              )}

            {topping.type === 'csv' &&
              (csvColumns.length === 0 || csvData.length === 0) && (
                <div>
                  <Alert
                    message="CSVデータが読み込めません"
                    description="CSVファイルの内容を読み込めませんでした。ファイルを再アップロードしてください。"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  <div>
                    <Text type="secondary">ファイル再アップロード</Text>
                    <div style={{ marginTop: 8 }}>
                      <Upload
                        accept=".csv"
                        maxCount={1}
                        fileList={fileList}
                        onChange={({ fileList }) => setFileList(fileList)}
                        beforeUpload={file => {
                          handleFileUpload(file);
                          return false;
                        }}
                      >
                        <Button icon={<UploadOutlined />}>
                          新しいCSVファイルをアップロード
                        </Button>
                      </Upload>
                    </div>
                  </div>
                </div>
              )}

            {topping.type === 'pdf' && (
              <div>
                <Text type="secondary">PDFファイル情報</Text>
                <div
                  style={{
                    marginTop: 8,
                    padding: 12,
                    backgroundColor: '#fafafa',
                    borderRadius: 4,
                    border: '1px solid #d9d9d9',
                  }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>ファイル名: </Text>
                    <Text>{topping.file_name || 'PDF ファイル'}</Text>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>文字数: </Text>
                    <Text>
                      {topping.content?.length.toLocaleString() || 0} 文字
                    </Text>
                  </div>
                  <div>
                    <Text strong>最終更新: </Text>
                    <Text>
                      {new Date(
                        topping.updated_at || topping.created_at,
                      ).toLocaleString()}
                    </Text>
                  </div>
                </div>

                <Divider />

                <div>
                  <Text type="secondary">ファイル再アップロード（任意）</Text>
                  <div style={{ marginTop: 8 }}>
                    <Upload
                      accept=".pdf"
                      maxCount={1}
                      fileList={fileList}
                      onChange={({ fileList }) => setFileList(fileList)}
                      beforeUpload={file => {
                        handleFileUpload(file);
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />}>
                        新しいPDFファイルをアップロード
                      </Button>
                    </Upload>
                  </div>
                  <Alert
                    message="ファイル再アップロードについて"
                    description="新しいPDFファイルをアップロードすると、既存のコンテンツが置き換えられます。"
                    type="warning"
                    showIcon
                    style={{ marginTop: 12 }}
                  />
                </div>
              </div>
            )}
          </Card>

          {/* 高度な設定（CSVとPDFのみ） */}
          {(topping.type === 'csv' || topping.type === 'pdf') && (
            <Card title="高度な設定" style={{ marginBottom: 24 }}>
              <Collapse
                ghost
                size="small"
                items={[
                  {
                    key: 'rag',
                    label: (
                      <Space>
                        <SettingOutlined />
                        <span style={{ color: '#666' }}>
                          RAG設定（AI検索用）
                        </span>
                      </Space>
                    ),
                    children: (
                      <div
                        style={{
                          background: '#fafafa',
                          padding: '16px',
                          borderRadius: '6px',
                        }}
                      >
                        <Alert
                          message="注意"
                          description="これらの設定を変更すると、AI検索の品質に影響する可能性があります。よくわからない場合は変更しないでください。"
                          type="warning"
                          showIcon
                          style={{ marginBottom: 16 }}
                        />

                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item
                              name="rag_enabled"
                              label="RAG有効化"
                              valuePropName="checked"
                            >
                              <Switch
                                checkedChildren="有効"
                                unCheckedChildren="無効"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              name="chunk_size"
                              label="チャンクサイズ"
                              tooltip="テキストを分割する際の1チャンクあたりの文字数（推奨: 1000）"
                            >
                              <InputNumber
                                min={100}
                                max={5000}
                                step={100}
                                style={{ width: '100%' }}
                                addonAfter="文字"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              name="chunk_overlap"
                              label="オーバーラップ"
                              tooltip="隣接するチャンク間で重複させる文字数（推奨: 200）"
                            >
                              <InputNumber
                                min={0}
                                max={1000}
                                step={50}
                                style={{ width: '100%' }}
                                addonAfter="文字"
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Form.Item
                          label="信頼度"
                          name="strength"
                          tooltip="この情報の信頼度（0.1〜2.0）"
                        >
                          <Slider
                            min={0.1}
                            max={2.0}
                            step={0.1}
                            marks={{
                              0.1: '低',
                              1.0: '標準',
                              2.0: '高',
                            }}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </div>
                    ),
                  },
                ]}
              />
            </Card>
          )}
        </Form>

        {/* フッター */}
        <div style={{ marginTop: 24 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Button
                size="large"
                onClick={handleClose}
                style={{ width: '100%' }}
              >
                キャンセル
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                size="large"
                onClick={handleUpdate}
                loading={loading}
                style={{ width: '100%' }}
              >
                更新
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    </Modal>
  );
};

export default EditToppingWizard;
